import { useMemo } from 'react';
import { useProductCatalog } from './useProductCatalog';
import { searchProducts } from './useFulltextSearch';
import type { Product, FilterMode } from '../types/product';

export interface UseProductsParams {
  name?: string;
  categories?: string[];
  territories?: string[];
  applications?: string[];
  indications?: string[];
  marketingClaims?: string[];
  partners?: string[];
  agreements?: string[];
  isNew?: boolean;
  hasNewStudy?: boolean;
  applicationMode?: FilterMode;
  indicationMode?: FilterMode;
  marketingClaimMode?: FilterMode;
}

function matchesRelation(
  product: { id: number; name: string }[],
  filterIds: string[],
  mode: FilterMode,
): boolean {
  if (filterIds.length === 0) return true;
  const check = (id: string) => product.some((r) => String(r.id) === id);
  return mode === 'AND' ? filterIds.every(check) : filterIds.some(check);
}

export function filterProducts(products: Product[], params: UseProductsParams): Product[] {
  let result = products;

  if (params.name) {
    result = searchProducts(result, params.name);
  }

  if (params.categories?.length)
    result = result.filter((p) => p.category && params.categories!.includes(String(p.category.id)));

  const territoryFilter = params.territories?.length ? params.territories : null;
  const agreementFilter = params.agreements?.length ? params.agreements : null;

  if (territoryFilter || agreementFilter) {
    const wantNone = agreementFilter?.includes('none') ?? false;
    const typedAgreements = agreementFilter?.filter((a) => a !== 'none') ?? [];

    result = result.filter((p) => {
      // When both filters are active, the agreement must live IN one of the
      // selected countries — not in some unrelated territory.
      if (territoryFilter && agreementFilter) {
        const matching = p.territories.filter((t) => territoryFilter.includes(String(t.id)));
        if (matching.length === 0) return false;
        if (typedAgreements.length > 0 && matching.some((t) => t.agreementType && typedAgreements.includes(t.agreementType))) {
          return true;
        }
        if (wantNone && matching.every((t) => !t.agreementType)) {
          return true;
        }
        return false;
      }

      if (territoryFilter) {
        return p.territories.some((t) => territoryFilter.includes(String(t.id)));
      }

      // Only agreement filter active.
      if (typedAgreements.length > 0 && p.territories.some((t) => t.agreementType && typedAgreements.includes(t.agreementType))) {
        return true;
      }
      if (wantNone && (p.territories.length === 0 || p.territories.every((t) => !t.agreementType))) {
        return true;
      }
      return false;
    });
  }

  if (params.applications?.length)
    result = result.filter((p) => matchesRelation(p.applications, params.applications!, params.applicationMode ?? 'OR'));

  if (params.indications?.length)
    result = result.filter((p) => matchesRelation(p.indications, params.indications!, params.indicationMode ?? 'OR'));

  if (params.marketingClaims?.length)
    result = result.filter((p) => matchesRelation(p.marketingClaims, params.marketingClaims!, params.marketingClaimMode ?? 'OR'));

  if (params.partners?.length)
    result = result.filter((p) => p.partner && params.partners!.includes(String(p.partner.id)));

  if (params.isNew) result = result.filter((p) => p.isNew);
  if (params.hasNewStudy) result = result.filter((p) => p.hasNewStudy);

  return result;
}

/**
 * Returns filtered products from the local catalog cache.
 * No API calls — all filtering happens client-side.
 */
export function useProducts(params: UseProductsParams = {}) {
  // isPending instead of isLoading: on an offline cold start without a
  // persisted catalog the query sits at pending/paused (offlineFirst waits
  // for connectivity). isLoading (= pending && fetching) would drop to false
  // there and the UI would claim "no products" although nothing was ever
  // loaded (VIVATISPDB-435).
  const { data: catalog, isPending } = useProductCatalog();

  const products = useMemo(
    () => (catalog ? filterProducts(catalog, params) : []),
    [catalog, params],
  );

  return { products, totalItems: products.length, isLoading: isPending, isError: false };
}

/**
 * Returns a single product from the local catalog cache.
 * No API call — reads directly from the cached catalog.
 */
export function useProduct(id: number | string) {
  // See useProducts: isPending keeps the detail page in its loading state
  // while the catalog was never fetched (offline cold start) instead of
  // rendering "Product not found".
  const { data: catalog, isPending } = useProductCatalog();

  const product = useMemo(
    () => catalog?.find((p) => p.id === Number(id)),
    [catalog, id],
  );

  return { data: product, isLoading: isPending, isError: false };
}
